import {
  computed,
  signal,
  Signal,
  untracked,
  ValueEqualityFn,
  WritableSignal,
  isDevMode,
} from '@angular/core';
import { create, Draft } from 'mutative';

/**
 * Dev-mode brand stamped onto every object frozen by a store, so a paused
 * `not extensible` exception shows which store the object leaked from:
 * inspect `obj[Symbol.for('fc-store.frozenBy')]` in the debugger.
 */
const FROZEN_BY = Symbol.for('fc-store.frozenBy');

interface DevtoolsConnection {
  init(state: unknown): void;
  send(action: { type: string }, state: unknown): void;
}

interface DevtoolsExtension {
  connect(options: { name: string }): DevtoolsConnection;
}

export abstract class Store<T extends object> {
  private readonly _state: WritableSignal<T>;
  readonly selectors: { [K in keyof T]: Signal<T[K]> };
  readonly state: Signal<T>;
  // Built from the initial state object, so optional keys of T must still be
  // present there (e.g. `{ selected: undefined }`) to be selectable.
  private readonly stateKeys: Set<PropertyKey>;
  // Tracks already-frozen objects across updates so dev freezing only walks
  // the parts of the state tree that changed.
  private readonly frozen = new WeakSet<object>();
  // One-way bridge to the Redux DevTools browser extension (dev mode only).
  private readonly devtools: DevtoolsConnection | null;
  private lastNotifiedState: T | null = null;

  private readonly initialStateRef: T;

  protected constructor(initialState: T) {
    const state = this.freezeForDev(initialState);

    this.initialStateRef = state;
    this._state = signal(state);
    this.state = this._state.asReadonly();
    this.stateKeys = new Set(Reflect.ownKeys(initialState));
    this.devtools = this.connectDevtools(state);

    this.selectors = new Proxy({} as { [K in keyof T]: Signal<T[K]> }, {
      get: (target, prop, receiver) => {
        if (typeof prop === 'symbol' || prop in target) {
          return Reflect.get(target, prop, receiver);
        }

        if (!this.stateKeys.has(prop)) {
          if (isDevMode()) {
            throw new Error(
              `[Store] Unknown selector "${String(prop)}" requested from ${this.constructor.name}. ` +
                `If "${String(prop)}" is an optional state key, include it in the initial state (e.g. { ${String(prop)}: undefined }).`,
            );
          }

          return undefined;
        }

        const key = prop as keyof T;
        Object.assign(target, {
          [key]: computed(() => this._state()[key]),
        });
        return target[key];
      },
    });
  }

  /**
   * Updates the state using a draft mutation (via mutative).
   * @param fn - A function that receives a draft of the state and mutates it.
   * @param label - Optional action name shown in Redux DevTools.
   */
  protected draftUpdate(fn: (draft: Draft<T>) => void, label?: string): void {
    this._state.update((prev) => {
      const [draft, finalize] = create(prev);
      fn(draft);
      return this.freezeForDev(finalize());
    });
    this.notifyDevtools(label ?? 'draftUpdate');
  }

  /**
   * Updates the state with a partial state object, or with a function that
   * derives a partial from the latest state when the next value depends on
   * the current one.
   * @param partialState - A partial state object, or a function receiving the latest state and returning one.
   * @param label - Optional action name shown in Redux DevTools.
   */
  protected update(
    partialState: Partial<T> | ((state: Readonly<T>) => Partial<T>),
    label?: string,
  ): void {
    this._state.update((prev) => {
      const partial =
        typeof partialState === 'function' ? partialState(prev) : partialState;

      if (!this.hasChanges(prev, partial)) {
        return prev;
      }

      return this.freezeForDev({ ...prev, ...partial });
    });
    this.notifyDevtools(label ?? 'update');
  }

  /**
   * Restores the initial state, or only the given keys of it.
   * Replaces hand-written `reset()` methods that re-declare the initial state.
   */
  protected resetState(...keys: Array<keyof T>): void {
    if (keys.length === 0) {
      this._state.set(this.initialStateRef);
      this.notifyDevtools('resetState');
      return;
    }

    const partial: Partial<T> = {};
    for (const key of keys) {
      partial[key] = this.initialStateRef[key];
    }
    this.update(partial, 'resetState');
  }

  /**
   * Creates a computed signal from the state.
   * @param selectorFn - A function that derives a value from the state.
   * @param options - Optional `equal` function so selectors returning fresh
   * arrays/objects don't re-emit when the derived value is equivalent.
   */
  public selectSignal<R>(
    selectorFn: (state: T) => R,
    options?: { equal?: ValueEqualityFn<R> },
  ): Signal<R> {
    return computed(() => selectorFn(this._state()), options);
  }

  private connectDevtools(initialState: T): DevtoolsConnection | null {
    if (!isDevMode() || typeof window === 'undefined') {
      return null;
    }

    const extension = (
      window as { __REDUX_DEVTOOLS_EXTENSION__?: DevtoolsExtension }
    ).__REDUX_DEVTOOLS_EXTENSION__;
    if (!extension) {
      return null;
    }

    const connection = extension.connect({ name: this.constructor.name });
    connection.init(initialState);
    this.lastNotifiedState = initialState;
    return connection;
  }

  private notifyDevtools(label: string): void {
    if (!this.devtools) {
      return;
    }

    // untracked: update()/draftUpdate() may run synchronously inside a
    // reactive context (e.g. a component effect calling a loader); reading
    // the state signal here would subscribe that context to every future
    // state change and create request loops in dev mode.
    const state = untracked(this._state);
    if (state === this.lastNotifiedState) {
      return;
    }

    this.lastNotifiedState = state;
    this.devtools.send({ type: label }, state);
  }

  private hasChanges(prev: T, partialState: Partial<T>): boolean {
    return Reflect.ownKeys(partialState).some((key) => {
      return !Object.is(prev[key as keyof T], partialState[key as keyof T]);
    });
  }

  private freezeForDev<V>(value: V): V {
    if (!isDevMode()) {
      return value;
    }

    return this.deepFreeze(value);
  }

  private deepFreeze<V>(value: V): V {
    if (
      typeof value !== 'object' ||
      value === null ||
      this.frozen.has(value) ||
      Object.isFrozen(value)
    ) {
      return value;
    }

    const prototype = Object.getPrototypeOf(value);
    if (
      !Array.isArray(value) &&
      prototype !== Object.prototype &&
      prototype !== null
    ) {
      return value;
    }

    this.frozen.add(value);
    Object.defineProperty(value, FROZEN_BY, {
      value: this.constructor.name,
      enumerable: false,
    });
    Object.freeze(value);

    for (const key of Reflect.ownKeys(value)) {
      this.deepFreeze((value as Record<PropertyKey, unknown>)[key]);
    }

    return value;
  }
}
